import * as React from 'react';
import { Dialog } from "@progress/kendo-react-dialogs";
import { ComboBoxChangeEvent, MultiColumnComboBox, MultiSelect, MultiSelectChangeEvent } from '@progress/kendo-react-dropdowns';
import { getUserPortalRoles, getCurrentIdpClientsManagement, getAccessibleAppsWithRoles, updateUser, updateUserMaster, updateUserAppRoles } from '../../ApiUtils/ApiUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { UserRepresentation } from '../../Types/UserRepresentation';
import { Grid, GridColumn, GridItemChangeEvent, GridToolbar } from '@progress/kendo-react-grid';
import { getUserDashboards } from '../../ApiUtils/ApiUtils';
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';

type TabItemKinds = "info" | "roles" | "attributes" | "dashboards";


interface AttributeTemplateItem {
    text: string;
    keyTemplate: string;
    valueTemplate: string;
    valuePlaceHolder?: string;
    valueDesc: string;
    icon?: string;
}

const defaultAttributeTemplate: AttributeTemplateItem = {
    text: 'Blank',
    keyTemplate: '',
    valueTemplate: '',
    valueDesc: 'Blank attribute'
}

const newAttributeTemplates: AttributeTemplateItem[] = [defaultAttributeTemplate, {
    text: 'ModellicaPro tenant',
    keyTemplate: 'modellica_pro##dev##tenant',
    valueTemplate: '',
    valueDesc: 'group names as ## separated value'
}];

const attributeTemplateColumns = [
    { field: "text", header: "Name", width: "200px" },
    { field: "keyTemplate", header: "Key", width: "300px" },
    { field: "valueDesc", header: "Description", width: "300px" },
];

interface AttributeItem {
    __id: number;
    key: string;
    value: string;
    inEdit: boolean;
}

interface UserEditFormState {
    Realm: string;
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    password: string;
    passwordConfirmation: string;
    passwordTemporary: boolean;
    isAdmin: boolean;
    isRoot: boolean;
    portalAccess: boolean;
    submitErrors: any;
    portalRoles: any;
    activeTab: TabItemKinds;
    appsWithRoles: any;
    rolesCompanyAccess: {
        roles: any[];
        activeRoles: any[];
    };
    isLoading: boolean;
    isPermissionLoading: boolean;
    submitLoading: boolean;
    editFormOpen?: boolean;
    [key: string]: any;
    hasError: boolean;
    hasPermissionError: boolean;
    attributes?: AttributeItem[];
    currentAttributeTemplateOption: AttributeTemplateItem | null;
    isPasswordFocused: boolean;
    dashboards?: { name: string; type: string }[];
    emailVerified?: boolean;
    showPassword: boolean,
    showConfirmPassword: boolean,
}

export interface UserEditFormProps {
    item: UserRepresentation & { editable?: boolean };
    cancelEdit: () => void;
    submitErrors: any[];
    isRoot: boolean;
    isAdmin: boolean;
    currentCompany?: string;
    handleFormClose: () => void;
}


class UserEditForm extends React.Component<UserEditFormProps, UserEditFormState> {
    public constructor(props: UserEditFormProps) {
        super(props);
        this.state = {
            id: this.props.item.id ?? '',
            username: this.props.item.username ?? '',
            firstName: this.props.item.firstName ?? '',
            lastName: this.props.item.lastName ?? '',
            password: "",
            passwordConfirmation: "",
            passwordTemporary: false,
            isAdmin: false,
            isRoot: false,
            portalAccess: false,
            submitErrors: this.props.submitErrors,
            portalRoles: {},
            activeTab: "info",
            appsWithRoles: {},
            rolesCompanyAccess: { roles: [], activeRoles: [] },
            isLoading: true,
            isPermissionLoading: true,
            submitLoading: false,
            hasError: false,
            hasPermissionError: false,
            attributes: this.mapAttributes(this.props.item.attributes),
            currentAttributeTemplateOption: defaultAttributeTemplate,
            Realm: "",
            loadingErrorMessage: "Error occurred while loading the data",
            portalRolesVisible: false,
            isPasswordFocused: false,
            dashboards: [],
            emailVerified: this.props.item.emailVerified,
            showPassword: false,
            showConfirmPassword: false,
        };
    }

    componentDidMount() {
        if (this.props.currentCompany && this.props.item.id) {
            this.loadData(this.props.currentCompany, this.props.item.id);
        }
    }

    public handleEditSubmit = async () => {
        this.setState({
            "submitErrors": [],
            submitLoading: true
        });

        if (this.state.password != this.state.passwordConfirmation) {
            this.setState({
                "submitErrors": ["Passwords don't match"],
                submitLoading: false
            });
            return;
        }

        // prepare attributes json
        let attributes = this.state.attributes?.filter(attribute => attribute.key).reduce<{[key: string]: string}>((result, current) => {
            result[current.key] = current.value;
            return result;
        }, {});

        const data = {
            "id": this.props.item.id,
            "username": this.props.item.username,
            "firstName": this.state.firstName,
            "lastName": this.state.lastName,
            "password": this.state.password,
            "passwordTemporary": this.state.passwordTemporary,
            "isAdmin": this.state.isAdmin,
            "isRoot": this.state.isRoot,
            "portalAccess": this.state.portalAccess,
            "attributes": attributes,
            "Realm": this.state.Realm,
            "emailVerified": this.state.emailVerified
        };

        try {
            let result = null;
            if (this.props.isRoot) {
                result = await updateUserMaster(data);
            } else {
                result = await updateUser(data);
            }

            let responseData;
            if (typeof result.data === "string") {
                responseData = JSON.parse(result.data);
                if (responseData.error_description) {
                    this.setState({
                        submitErrors: responseData.error_description,
                        submitLoading: false
                    });
                    return;
                }
            } 

            await updateUserAppRoles({ userInfo: { realm: this.props.currentCompany, userId: this.props.item.id }, appsWithRoles: this.state.appsWithRoles }).then(response => {
                this.setState({ editFormOpen: false });
                this.props.handleFormClose();
            });
        }
        catch (ex) {
            console.log(ex);
        }


        let appsWithRolesToSend = this.state.appsWithRoles;

        //delete "$id" element from activeRoles object
        for (const app in appsWithRolesToSend) {
            if (app != "$id") {
                for (const key in appsWithRolesToSend[app]) {
                    if (key == "activeRoles") {
                        console.log(appsWithRolesToSend[app][key]);
                        appsWithRolesToSend[app][key].forEach( (element:any )=> {
                            delete element["$id"];
                        })
                    }
                }
            }
        }
        
        await updateUserAppRoles({ userInfo: { realm: this.props.currentCompany, userId: this.props.item.id }, appsWithRoles: appsWithRolesToSend }).then(response => {
            this.setState({ editFormOpen: false });
            this.props.handleFormClose();
        });
    }

    private getRealmClients = async(realmName: string) => {
        return await getCurrentIdpClientsManagement(realmName).then((data) => data);
    }

    private loadData(company: string, userId: string) {
        getUserPortalRoles(company, userId).then(response => {
            var response = JSON.parse(response);
            this.getRealmClients(company).then((data)=>{
              let displayInConsole = (Object.values(JSON.parse(data)).filter(v => (v as {clientId: string}).clientId == 'gds-portal')[0] as {alwaysDisplayInConsole: boolean}).alwaysDisplayInConsole
              if (displayInConsole){
                this.setState({
                  isRoot: response.root,
                  isAdmin: response.admin,
                  portalAccess: response.access,
                  isPermissionLoading: false,
                  Realm: company,
                  portalRolesVisible: true
                });
              }
            })
        }).catch(() => {
            this.setState({
                isPermissionLoading: false,
                hasPermissionError: true,
                hasError: false,
                portalRolesVisible: false,
  
            });
        });
        getAccessibleAppsWithRoles(company, userId).then(response => {
            this.setState({
                appsWithRoles: response,
                isLoading: false,
                rolesCompanyAccess: response["GDSLINK-AUTHENTICATION-PORTAL"],
            });
        }).catch(() => {
            this.setState({
                isLoading: false,
                hasError: true,
                loadingErrorMessage: "Unable to load applications and roles."
            });
        });
        getUserDashboards().then(response => {
            const data = response.data;
            const validDashboards = Array.isArray(data)
                ? data.filter((d: any) => d.name && d.type)
                : [];

            this.setState({ dashboards: validDashboards });
        }).catch(() => {
            this.setState({ dashboards: [] });
        });


    }

    private toggleTab = (tab: TabItemKinds) => () => {
        this.setState({ activeTab: tab });
    }

    private inputChangeHandler = (event: any) => {
        this.setState({ [event.target.name]: event.target.value });
    }

    private checkboxChangeHandler = (event: any) => {
        this.setState({ [event.target.name]: event.target.checked });
    }

    private mapAttributes = (source: any): AttributeItem[] | undefined => {
        return source !== undefined ? Object.keys(source).map((key, index) => {
            return {
                __id: index,
                key,
                value: this.mapAttributeValue(source[key]),
                inEdit: true
            };
        }) : undefined;
    }

    private mapAttributeValue = (value: any) => {
        if (Array.isArray(value)) {
            return value.join("##");
        }
        return value;
    }

    private addNewAttribute = () => {
        const newDataItem = {
            __id: this.state.attributes?.length ?? 0,
            key: this.state.currentAttributeTemplateOption?.keyTemplate ?? '',
            value: this.state.currentAttributeTemplateOption?.valueTemplate ?? '',
            inEdit: true
        } as AttributeItem;

        this.setState({
            attributes: [newDataItem, ...this.state.attributes ?? []],
        });
    }

    private handleAttributeItemChange = (e: GridItemChangeEvent) => {
        let newAttributes = this.state.attributes?.map(item => {
            return item.__id === e.dataItem.__id
                ? { ...item, [e.field || ""]: e.value }
                : item;
        });
        this.setState({
            attributes: newAttributes
        });
    }

    private handleAttributeTemplateChange = (e: ComboBoxChangeEvent) => {
        this.setState({
            currentAttributeTemplateOption: e.value
        });
    }

    private filterAttributeTemplate = () => {
        //let filteredList = newAttributeTemplates.filter(attributeTemplate => !attributeTemplate.keyTemplate || (this.state.attributes?.filter(attribute => attribute.key === attributeTemplate.keyTemplate).length ?? 0) === 0);
        return newAttributeTemplates;
    }

    private canAddAttribute = () => {
        return (!this.state.currentAttributeTemplateOption?.keyTemplate) || (!this.state.attributes?.some(attribute => attribute.key === this.state.currentAttributeTemplateOption?.keyTemplate) ?? true);
    }

    private handlePasswordFocus = () => {
        this.setState({ isPasswordFocused: true });
    };

    private handlePasswordBlur = () => {
        this.setState({ isPasswordFocused: false });
    };

    private isMinLength = () => {
        return this.state.password.length >= 12;
    }

    private hasUppercase = () => {
        return /[A-Z]/.test(this.state.password);
    }
    private hasLowercase = () => {
        return /[a-z]/.test(this.state.password);
    }

    private hasNumber = () => {
        return /[0-9]/.test(this.state.password);
    }

    private hasSpecialChar = () => {
        return /[^A-Za-z0-9]/.test(this.state.password);
    }

    private doPasswordsMatch = () => {
        return this.state.password && this.state.password === this.state.passwordConfirmation;
    };
    private isPasswordValid = () => {
        return this.isMinLength() && this.hasUppercase() && this.hasLowercase() && this.hasNumber() && this.hasSpecialChar();
    }

    private toggleShowPassword = () => {
        this.setState((prevState) => ({ showPassword: !prevState.showPassword }));
    };

    private toggleShowConfirmPassword = () => {
        this.setState((prevState) => ({ showConfirmPassword: !prevState.showConfirmPassword }));
    };

    public render() {
        const isPasswordValid = this.isPasswordValid();
        return (
            <>
                <Dialog width={600} height={"98%"} title={`Edit user ${this.props.item.username}`} onClose={this.props.cancelEdit}>
                    <div style={{ position: "relative",height:"100%" }}>
                        <div className="spinner-container" style={{ display: ((this.state.isLoading || this.state.submitLoading || this.state.hasError)?'block':'none')}}>
                            {(this.state.isLoading || this.state.submitLoading) && <FontAwesomeIcon icon={faSpinner} size="2x" spin={true} />}
                            {this.state.hasError && <FontAwesomeIcon className="text-error" icon={faExclamationCircle} size="2x" title="Error" />}
                            <span style={{ color: "#f99", display: (this.state.hasError ? 'block' : 'none') }}>
                                {this.state.loadingErrorMessage.split("|").map((er: any) => {
                                    return <div>{er}</div>
                                })
                                }
                            </span>
                        </div>
                        <div className={"tab-container"} >
                            <div id="info" className={"tab " + (this.state.activeTab === "info" ? "tab-selected" : "")} onClick={this.toggleTab("info")}>User Info</div>
                            <div id="roles" className={"tab " + (this.state.activeTab === "roles" ? "tab-selected" : "")} onClick={this.toggleTab("roles")} >Application Roles</div>
                            <div id="attributes" className={"tab " + (this.state.activeTab === "attributes" ? "tab-selected" : "")} onClick={this.toggleTab("attributes")} >Attributes</div>
                            <div id="dashboards" className={"tab " + (this.state.activeTab === "dashboards" ? "tab-selected" : "")} onClick={this.toggleTab("dashboards")}>Dashboards</div>

                        </div>
                        <div id="overlay">
                        </div>

                        {this.state.activeTab === "info" && (
                            <div>
                               
                                <form autoComplete="off" style={{ padding: "15px 15px 15px 15px" }} id="#edit_form" >
                                    <input type="hidden" name="Realm" value={this.state.realm} />
                                    <input type="hidden" name="id" id="id" value={this.props.item.id} />
                                    <label htmlFor="userName">Username (email)</label><br />
                                    <input autoComplete="nope" type="email" name="username" id="username" className="field k-input k-input-md k-rounded-md k-input-solid" disabled={true} value={this.props.item.username} /> <br />
                                    <label htmlFor="firstName">First Name</label><br />
                                    <input autoComplete="off" onChange={this.inputChangeHandler} value={this.state.firstName} type="text" name="firstName" id="firstName" className="field k-input k-input-md k-rounded-md k-input-solid" /><br />
                                    <label htmlFor="lastName">Last Name</label><br />
                                    <input autoComplete="off"  onChange={this.inputChangeHandler} value={this.state.lastName} type="text" name="lastName" id="lastName" className="field k-input k-input-md k-rounded-md k-input-solid" /><br />
                                    <hr />
                                    <div>
                                        <span className={"half-section"}>
                                            <label htmlFor="password">Password</label><br />
                                            <div style={{ position: 'relative' }}>
                                                <input autoComplete="off" onChange={this.inputChangeHandler}
                                                    onFocus={this.handlePasswordFocus} onBlur={this.handlePasswordBlur}
                                                    type={this.state.showPassword ? "text" : "password"}
                                                    name="password" id="password"
                                                    className="field k-input k-input-md k-rounded-md k-input-solid"
                                                    style={{ borderColor: this.state.password ? (isPasswordValid ? 'green' : 'red') : undefined }} />
                                                <span
                                                    className="password-eye-edit"
                                                    onClick={this.toggleShowPassword}
                                                >
                                                    <FontAwesomeIcon icon={this.state.showPassword ? faEyeSlash : faEye} />
                                                </span>
                                                <br style={{ userSelect: 'none' }} />
                                                {this.state.password && this.state.isPasswordFocused && !isPasswordValid && (
                                                <div className="passwordTooltip">
                                                    Password must include:
                                                    <ul style={{ margin: 0, paddingLeft: '25px' }}>
                                                        <li style={{ color: this.isMinLength() ? 'green' : 'red' }}>At least 12 characters</li>
                                                        <li style={{ color: this.hasUppercase() ? 'green' : 'red' }}>One uppercase letter</li>
                                                        <li style={{ color: this.hasLowercase() ? 'green' : 'red' }}>One lowercase letter</li>
                                                        <li style={{ color: this.hasNumber() ? 'green' : 'red' }}>One number</li>
                                                        <li style={{ color: this.hasSpecialChar() ? 'green' : 'red' }}>One special character</li>
                                                    </ul>
                                                </div>
                                            )}
                                            </div>
                                            <label htmlFor="passwordConfirmation"> Password confirmation</label><br />
                                            <div style={{ position: 'relative' }}>
                                                <input autoComplete="off" onChange={this.inputChangeHandler} value={this.state.passwordConfirmation}
                                                    type={this.state.showConfirmPassword ? "text" : "password"}
                                                    name="passwordConfirmation" id="passwordConfirmation" className="field k-input k-input-md k-rounded-md k-input-solid"
                                                    style={{ borderColor: this.state.passwordConfirmation ? (this.doPasswordsMatch() ? 'green' : 'red') : undefined }} />
                                                <span
                                                    className="password-eye-edit"
                                                    onClick={this.toggleShowConfirmPassword}
                                                >
                                                    <FontAwesomeIcon icon={this.state.showConfirmPassword ? faEyeSlash : faEye} />
                                                </span>
                                                <br style={{ userSelect: 'none' }} />
                                            </div>
                                            <input autoComplete="off" onChange={this.checkboxChangeHandler} checked={this.state.passwordTemporary} type="checkbox" id="passwordTemporary" name="passwordTemporary" className={"field k-checkbox k-checkbox-md k-rounded-md gds-checkbox"} />
                                            <label htmlFor="passwordTemporary"> Password temporary?</label>
                                            <br />  <br />
                                    <div style={{ color: "red",width:"100%",textAlign:"center" }}>{this.state.submitErrors}</div>
                                        </span>
                                        <span className={"permissons-block"} style={{ display: (this.state.portalRolesVisible ? "inline-block":"none")}}>
                                            <div className="mb-2" style={{ width: "100%", textAlign: "center", display: "inline-block" }}>
                                                <span className={"edit-section-label"}>GDS Portal permissions&nbsp;</span>
                                                {this.state.isPermissionLoading && <FontAwesomeIcon icon={faSpinner} size="1x" spin={true} />}
                                                {this.state.hasPermissionError && <FontAwesomeIcon className="text-error" icon={faExclamationCircle} size="1x" title="Error" />}
                                            </div>
                                            <div>
                                                <input onChange={this.checkboxChangeHandler} checked={this.state.portalAccess} type="checkbox" id="portalAccess" name="portalAccess" className={"k-checkbox k-checkbox-md k-rounded-md gds-checkbox"} />
                                                <label htmlFor="portalAccess">Can access portal ? </label>
                                                <br /><br />
                                            <input type="checkbox" id="isAdmin" name="isAdmin" checked={this.state.isAdmin} onChange={this.checkboxChangeHandler} className={"k-checkbox k-checkbox-md k-rounded-md gds-checkbox"} />
                                            <label htmlFor="isAdmin">Portal admin of {this.props.currentCompany}</label><br /><br />
                                            {(this.props.isRoot && this.props.currentCompany == "GDSLINK") && (
                                                <span>
                                                    <input type="checkbox" id="isRoot" name="isRoot" checked={this.state.isRoot} onChange={this.checkboxChangeHandler} className={"k-checkbox k-checkbox-md k-rounded-md gds-checkbox"} />
                                                    <label htmlFor="isRoot">Portal root</label><br /><br />
                                                </span>
                                            )}
                                            </div>
                                        </span>
                                        <span className={"permissons-block"} style={{color: "#f99",fontSize:"0.8em",display: (!this.state.portalRolesVisible ? "inline-block" : "none") }}>
                                            Unable to load Portal permissions.
                                            <br />Is Portal application enabled for this company ?

                                        </span>
                                    </div>
                                    <hr />
                                    {((this.props.isAdmin || this.props.isRoot) && this.props.currentCompany === "GDSLINK" && this.state.rolesCompanyAccess) && (
                                        <span style={{ width: "100%", textAlign: "center", display: "inline-block" }}>
                                            <p className={"edit-section-label"}> Can manage users of company &nbsp;
                                                {(this.state.isLoading) && <FontAwesomeIcon icon={faSpinner} size="1x" spin={true} />}
                                                {this.state.hasError && <FontAwesomeIcon className="text-error" icon={faExclamationCircle} size="1x" title="Error" />}
                                            </p>
                                            <MultiSelect autoClose={false}
                                                onChange={
                                                    (event: MultiSelectChangeEvent) => {
                                                        let statusCopy = Object.assign({}, this.state);
                                                        statusCopy.rolesCompanyAccess.activeRoles = event.target.value;
                                                        this.setState(statusCopy);
                                                    }
                                                }
                                                data={this.state.rolesCompanyAccess["roles"]}
                                                value={this.state.rolesCompanyAccess["activeRoles"]}
                                                textField="name"
                                                dataItemKey="id" />
                                            <br /><br /><br />
                                        </span>)}
                                </form>
                            </div>)
                        }
                        {this.state.activeTab === "roles" && (
                            <div className={"scrollbar"} style={{ padding: "0px 15px 0px 15px", height: "720px", overflowY: "auto", backgroundColor: "" }}>
                                <div className="spinner-container" style={{ display: ((this.state.isLoading || this.state.submitLoading || this.state.hasError) ? 'block' : 'none') }}>
                                    {(this.state.isLoading || this.state.submitLoading) && <FontAwesomeIcon icon={faSpinner} size="1x" spin={true} />}
                                    {this.state.hasError && <FontAwesomeIcon className="text-error" icon={faExclamationCircle} size="1x" title="Error" />}
                                </div>
                                {Object.keys(this.state.appsWithRoles).filter(e => e !== "GDSLINK-AUTHENTICATION-PORTAL" && e !== "$id").map((e: any) => {
                                    return (
                                        <div key={this.state.appsWithRoles[e.toString()].id} >
                                            <div className="multi-select-label" >{this.state.appsWithRoles[e.toString()].name ?? this.state.appsWithRoles[e.toString()].clientId}:</div>
                                            <MultiSelect autoClose={false} onChange={(event: MultiSelectChangeEvent) => {
                                                let statusCopy = Object.assign({}, this.state);
                                                statusCopy.appsWithRoles[e.toString()].activeRoles = event.target.value;
                                                this.setState(statusCopy);
                                            }}
                                                data={this.state.appsWithRoles[e.toString()].roles}
                                                value={this.state.appsWithRoles[e.toString()].activeRoles}
                                                textField="name"
                                                dataItemKey="id" />
                                            <br /><br />
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                        {this.state.activeTab === "attributes" && (
                            <div className={"scrollbar"} style={{ padding: "0px 15px 0px 15px", height: "720px", overflowY: "auto", backgroundColor: "" }}>
                                <div className="spinner-container" style={{ display: ((this.state.isLoading || this.state.submitLoading || this.state.hasError) ? 'block' : 'none') }}>
                                    {(this.state.isLoading || this.state.submitLoading) && <FontAwesomeIcon icon={faSpinner} size="1x" spin={true} />}
                                    {this.state.hasError && <FontAwesomeIcon className="text-error" icon={faExclamationCircle} size="1x" title="Error" />}
                                </div>
                                <div className="h-100">
                                    <Grid
                                        data={this.state.attributes}
                                        style={{ height: '100%' }}
                                        editField="inEdit"
                                        onItemChange={this.handleAttributeItemChange}
                                    >
                                        <GridToolbar>
                                            <div className="k-input-groups">
                                                <MultiColumnComboBox
                                                    data={this.filterAttributeTemplate()}
                                                    columns={attributeTemplateColumns}
                                                    textField={"text"}
                                                    value={this.state.currentAttributeTemplateOption}
                                                    onChange={this.handleAttributeTemplateChange}
                                                    allowCustom={false}
                                                />
                                                <button
                                                    title="Add new"
                                                    className="k-button k-button-md k-rounded-md k-button-solid btn-primary"
                                                    onClick={this.addNewAttribute}
                                                    disabled={!this.canAddAttribute()}
                                                >
                                                    Add {this.state.currentAttributeTemplateOption?.text} attribute
                                                </button>
                                            </div>
                                        </GridToolbar>
                                        <GridColumn field="key" title="Key" editor="text" />
                                        <GridColumn field="value" title="Value" editor="text" />
                                    </Grid>
                                </div>
                            </div>
                        )}
                        {this.state.activeTab === "dashboards" && (
                            <div className="scrollbar" style={{ padding: "15px", height: "720px", overflowY: "auto" }}>
                                {this.state.dashboards?.length ? (
                                    <div>
                                        {this.props.item.editable && (
                                            <button
                                                className="btn btn-primary mb-3"
                                                onClick={() => window.open(`/dashboard-management/${this.props.currentCompany}`, '_blank')}
                                            >
                                                Manage Dashboard
                                            </button>
                                        )}
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Type</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {this.state.dashboards.map((dashboard, index) => (
                                                    <tr key={index}>
                                                        <td>{dashboard.name}</td>
                                                        <td>{dashboard.type}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div>No dashboards found.</div>
                                )}
                            </div>
                        )}
                        <br/>
                        <div className={"dialog-button-container"}>
                            <div className={"dialog-button-inner-container"}>
                                <input type="submit" value="Save" onClick={this.handleEditSubmit} disabled={this.state.submitLoading || this.state.isLoading} className={"btn btn-primary user-create-button"} />
                                <button type="button" onClick={this.props.cancelEdit} disabled={this.state.submitLoading} className={"btn btn-secondary user-create-button"}>Cancel</button>
                            </div>
                        </div>
                    </div>
                </Dialog>
            </>
        );
    }
}

export default UserEditForm;
